import {
  BuildingWithTrusses,
  BuildingWithoutTrusses,
  GableTruss,
  LeanToTruss,
  Order,
  OrderStatus,
  Truss,
} from '../../types';
import { DateUtils } from '../../utils';
import {
  BuildingCompletionStatus,
  Classification,
  PostSize,
  ProductionStage,
  ProductionStageStatus,
  StageConfigDef,
  TaskTrackerResponse,
  TravelerBuilding,
  TravelerOrderHeader,
  TravelerTruss,
  TrussSpecificationResponse,
  TrussSpecifications,
} from './types';

/** Canonical name for the Final QC stage — used for lock detection (BR-18/BR-19). */
export const FINAL_QC_STAGE_NAME = 'FINAL QC';

export function transformOrderToTravelerHeader(
  order: Order,
  dealerName?: string
): TravelerOrderHeader {
  return {
    id: order.id ?? 0,
    orderNumber: order.orderNumber,
    customerName: dealerName ?? '',
    orderDate: DateUtils.toDisplayDateTime(order.orderDate),
    dueDate: order.dueDate ? DateUtils.toDisplayDate(order.dueDate) : null,
    dueDateChangedAt: order.dueDateChangedAt ?? null,
    po: order.po,
    jobName: order.jobName,
    deliveryOption: order.pickup ? 'Pickup' : 'Delivery',
    location: order.shippingAddress
      ? `${order.shippingAddress.city}, ${order.shippingAddress.state}`
      : order.billingAddress
        ? `${order.billingAddress.city}, ${order.billingAddress.state}`
        : '',
    notes: order.notes ?? null,
    signedBy: order.signedBy ?? null,
    signedDate: order.signedDate ? DateUtils.toDisplayDateTime(order.signedDate) : null,
  };
}

function mapClassification(classification: string): Classification {
  const lower = classification?.toLowerCase() ?? '';
  if (lower.includes('monoslope')) return 'Monoslope';
  if (lower.includes('class1') || lower === 'class 1') return 'Class1';
  return 'Standard';
}

function mapPostSize(postSize: string): PostSize {
  if (postSize?.includes('10')) return '10x10';
  if (postSize?.includes('8')) return '8x8';
  return '6x6';
}

const DEFAULT_STAGE_DEFS: StageConfigDef[] = [
  { stageName: 'SAW', sequenceOrder: 1, isQc: false, isFinalQc: false },
  { stageName: 'SAW QC', sequenceOrder: 2, isQc: true, isFinalQc: false },
  { stageName: 'WELD', sequenceOrder: 3, isQc: false, isFinalQc: false },
  { stageName: 'WELD QC', sequenceOrder: 4, isQc: true, isFinalQc: false },
  { stageName: 'PAINT', sequenceOrder: 5, isQc: false, isFinalQc: false },
  { stageName: 'PAINT QC', sequenceOrder: 6, isQc: true, isFinalQc: false },
  { stageName: FINAL_QC_STAGE_NAME, sequenceOrder: 7, isQc: true, isFinalQc: true },
];

/**
 * Exported so callers without server data can fall back gracefully.
 * Prefer passing the server-fetched stage config whenever available.
 */
export { DEFAULT_STAGE_DEFS };

function resolveStageStatus(
  tracker: TaskTrackerResponse | undefined,
  _sequenceOrder: number,
  _mergedStages: Array<{ tracker?: TaskTrackerResponse; order: number }>,
  productionReleased: boolean
): ProductionStageStatus {
  if (tracker?.status === 'Complete') return 'complete';
  if (!productionReleased) return 'pending';
  if (tracker?.status === 'InProgress') return 'active';
  return 'pending';
}

// NOTE (BR-17): This function renders production stages using the server-provided per-manufacturer
// stage configuration. If stageConfigs is empty (e.g., during initial load), the DEFAULT_STAGE_DEFS
// fallback is used to avoid a blank UI. Carousel completion (computeBuildingCompletionStatuses)
// always checks for FINAL QC completion directly, so it is not affected by stage count.
function buildProductionStages(
  trussId: number,
  taskTrackerMap: Map<number, TaskTrackerResponse[]>,
  stageConfigs: StageConfigDef[],
  productionReleased: boolean
): ProductionStage[] {
  const defs = stageConfigs.length > 0 ? stageConfigs : DEFAULT_STAGE_DEFS;
  const trackers = taskTrackerMap.get(trussId) ?? [];
  const trackerByName = new Map<string, TaskTrackerResponse>();
  for (const t of trackers) {
    trackerByName.set(t.stageName.toUpperCase(), t);
  }

  const mergedStages = defs.map(def => ({
    def,
    order: def.sequenceOrder,
    tracker: trackerByName.get(def.stageName.toUpperCase()),
  }));

  return mergedStages.map(({ def, order, tracker }) => ({
    id: tracker?.id ?? -order,
    trussId,
    stageName: tracker?.stageName ?? def.stageName,
    stageOrder: tracker?.sequenceOrder ?? order,
    technicianId: tracker?.technicianId?.toString() ?? null,
    technicianName: tracker?.technicianName ?? null,
    date: tracker?.completedDate ?? null,
    initials: tracker?.initials ?? null,
    status: resolveStageStatus(tracker, order, mergedStages, productionReleased),
    isQcStage: def.isQc,
    isFinalQc: def.isFinalQc,
  }));
}

function mapApiSpecToTrussSpecifications(apiSpec: TrussSpecificationResponse): TrussSpecifications {
  return {
    trussDepth: apiSpec.trussDepth,
    overhang: apiSpec.overhang,
    lowerOverhang: apiSpec.lowerOverhang,
    upperOverhang: apiSpec.upperOverhang,
    post: apiSpec.post,
    cordMaterial: apiSpec.cordMaterial,
    cordAngle: apiSpec.cordAngle,
    topCord: apiSpec.topCord,
    bottomCord: apiSpec.bottomCord,
    drillHoleDim: apiSpec.drillHoleDim,
    topPlates: apiSpec.topPlates,
    topPlateHeight: apiSpec.topPlateHeight,
    endPlates: apiSpec.endPlates,
    angleLength: apiSpec.angleLength,
    crossTieLength: apiSpec.crossTieLength,
    carriageBoltsRequired: apiSpec.carriageBoltsRequired,
    carriageBoltSize: apiSpec.carriageBoltSize,
    carriageBoltKits: apiSpec.carriageBoltKits,
  };
}

export function buildSpecsMap(
  apiSpecs: TrussSpecificationResponse[]
): Map<number, TrussSpecificationResponse> {
  const map = new Map<number, TrussSpecificationResponse>();
  for (const spec of apiSpecs) {
    map.set(spec.trussId, spec);
  }
  return map;
}

function transformTruss(
  truss: Truss,
  buildingPostSize: string,
  windSpeed: number,
  snowLoad: number,
  taskTrackerMap: Map<number, TaskTrackerResponse[]>,
  specsMap: Map<number, TrussSpecificationResponse>,
  stageConfigs: StageConfigDef[],
  productionReleased: boolean
): TravelerTruss {
  const leanTo = truss.productType === 'LeanTo' ? (truss as LeanToTruss) : null;
  const overriddenPostSize =
    leanTo?.postSizeOverride &&
    leanTo.postSizeOverrideValue != null &&
    leanTo.postSizeOverrideValue !== ''
      ? leanTo.postSizeOverrideValue
      : null;
  const postSize = mapPostSize(overriddenPostSize ?? buildingPostSize);
  const classification = mapClassification(truss.classification);
  const isMonoslope =
    truss.productType === 'LeanTo' ? (truss as LeanToTruss).monoslopeTruss : false;

  const pitchStr =
    truss.productType === 'Gable'
      ? (truss as GableTruss).roofPitch
      : truss.productType === 'LeanTo'
        ? (truss as LeanToTruss).roofPitch
        : null;

  const overhangType =
    truss.productType === 'Gable'
      ? (truss as GableTruss).overhangType
      : truss.productType === 'LeanTo'
        ? (truss as LeanToTruss).overhangType
        : null;

  const overhangSize =
    truss.productType === 'Gable'
      ? ((truss as GableTruss).overhangSize ?? null)
      : truss.productType === 'LeanTo'
        ? ((truss as LeanToTruss).overhangSize ?? null)
        : null;

  const apiSpec = specsMap.get(truss.id);
  const specs = apiSpec ? mapApiSpecToTrussSpecifications(apiSpec) : null;
  const stages = buildProductionStages(truss.id, taskTrackerMap, stageConfigs, productionReleased);
  const allComplete = stages.length > 0 && stages.every(s => s.status === 'complete');

  return {
    id: truss.id,
    buildingId: truss.buildingId,
    productType: truss.productType,
    classification,
    sizeFeet: truss.sizeFeet,
    sizeInches: truss.sizeInches,
    quantity: truss.quantity,
    roofPitch: pitchStr ?? null,
    overhangType: overhangType ?? null,
    overhangSize: overhangSize,
    carriageBolts: truss.carriageBolts,
    monoslopeTruss: isMonoslope,
    postSize,
    postSizeOverride:
      truss.productType === 'LeanTo' ? !!(truss as LeanToTruss).postSizeOverride : false,
    postSizeOverrideValue:
      truss.productType === 'LeanTo'
        ? ((truss as LeanToTruss).postSizeOverrideValue ?? null)
        : null,
    windSpeed,
    snowLoad,
    notes: truss.notes ?? null,
    measurementInsidePost: truss.measurementInsidePost,
    isLocked: allComplete,
    specifications: specs,
    productionStages: stages,
  };
}

export function transformBuildingToTraveler(
  building: BuildingWithTrusses,
  order: Order,
  taskTrackerMap: Map<number, TaskTrackerResponse[]> = new Map(),
  specsMap: Map<number, TrussSpecificationResponse> = new Map(),
  stageConfigs: StageConfigDef[] = []
): TravelerBuilding {
  const productionReleased = order.status >= OrderStatus.IN_QUEUE;
  const gableTrusses = (building.gableTrusses ?? []).map(t =>
    transformTruss(
      t as Truss,
      building.postSize,
      order.windLoad,
      order.snowLoad,
      taskTrackerMap,
      specsMap,
      stageConfigs,
      productionReleased
    )
  );
  const headerTrusses = (building.headerTrusses ?? []).map(t =>
    transformTruss(
      t as Truss,
      building.postSize,
      order.windLoad,
      order.snowLoad,
      taskTrackerMap,
      specsMap,
      stageConfigs,
      productionReleased
    )
  );
  const leanToTrusses = (building.leanToTrusses ?? []).map(t =>
    transformTruss(
      t as Truss,
      building.postSize,
      order.windLoad,
      order.snowLoad,
      taskTrackerMap,
      specsMap,
      stageConfigs,
      productionReleased
    )
  );

  const allTrusses = [...gableTrusses, ...headerTrusses, ...leanToTrusses];
  let completionStatus: BuildingCompletionStatus = 'not_started';
  if (allTrusses.length > 0) {
    const allLocked = allTrusses.every(t => t.isLocked);
    const anyStarted = allTrusses.some(t => t.productionStages.some(s => s.status === 'complete'));
    if (allLocked) completionStatus = 'complete';
    else if (anyStarted) completionStatus = 'in_progress';
  }

  return {
    id: building.id,
    name: building.name,
    description: building.additionalNotes ?? null,
    width: building.width ?? null,
    length: building.length ?? null,
    height: building.height ?? null,
    baySpacing: building.baySpacing,
    postSize: mapPostSize(building.postSize),
    geographicLocation: order.buildingState ?? null,
    liveLoad: order.liveLoad,
    deadLoad: order.deadLoad,
    windSpeed: order.windLoad,
    snowLoad: order.snowLoad,
    confirmedLoadRating: order.confirmedLoadRating ?? false,
    isPermanentDwelling: building.isPermanentDwelling,
    completionStatus,
    gableTrusses,
    headerTrusses,
    leanToTrusses,
  };
}

export function buildTaskTrackerMap(
  trackers: TaskTrackerResponse[]
): Map<number, TaskTrackerResponse[]> {
  const map = new Map<number, TaskTrackerResponse[]>();
  for (const tracker of trackers) {
    const existing = map.get(tracker.trussId);
    if (existing) {
      existing.push(tracker);
    } else {
      map.set(tracker.trussId, [tracker]);
    }
  }
  return map;
}

/**
 * Returns true when every truss in every building of the order has passed FINAL QC.
 * Mirrors the server's AreAllTrussesCompleteByOrderIdAsync: an order with no trusses returns false.
 * Used to gate the Work Order footer QC sign-off, which can only be recorded once all trusses
 * are through FINAL QC. Note this is independent of the order-level QC sign-off itself.
 */
export function areAllTrussesThroughFinalQc(
  buildings: BuildingWithTrusses[],
  taskTrackerMap: Map<number, TaskTrackerResponse[]>
): boolean {
  if (buildings.length === 0) return false;

  // Every building must have at least one truss assigned for the order to be completable.
  const anyBuildingWithoutTrusses = buildings.some(b => {
    const count =
      (b.gableTrusses?.length ?? 0) +
      (b.headerTrusses?.length ?? 0) +
      (b.leanToTrusses?.length ?? 0);
    return count === 0;
  });
  if (anyBuildingWithoutTrusses) return false;

  const allTrussIds = buildings.flatMap(b => [
    ...(b.gableTrusses ?? []).map(t => t.id),
    ...(b.headerTrusses ?? []).map(t => t.id),
    ...(b.leanToTrusses ?? []).map(t => t.id),
  ]);

  if (allTrussIds.length === 0) return false;

  return allTrussIds.every(trussId => {
    const trackers = taskTrackerMap.get(trussId) ?? [];
    return trackers.some(
      t => t.stageName.toUpperCase() === FINAL_QC_STAGE_NAME && t.status === 'Complete'
    );
  });
}

export function computeBuildingCompletionStatuses(
  buildings: BuildingWithTrusses[],
  taskTrackerMap: Map<number, TaskTrackerResponse[]>
): Map<number, BuildingCompletionStatus> {
  const statusMap = new Map<number, BuildingCompletionStatus>();

  for (const building of buildings) {
    const allTrussIds = [
      ...(building.gableTrusses ?? []).map(t => t.id),
      ...(building.headerTrusses ?? []).map(t => t.id),
      ...(building.leanToTrusses ?? []).map(t => t.id),
    ];

    if (allTrussIds.length === 0) {
      statusMap.set(building.id, 'no_trusses');
      continue;
    }

    let allComplete = true;
    let anyStarted = false;

    for (const trussId of allTrussIds) {
      const trackers = taskTrackerMap.get(trussId) ?? [];
      // BR-18/BR-19: A truss is locked when its FINAL QC stage is complete, regardless
      // of total stage count. Using FINAL QC as the lock indicator is manufacturer-agnostic
      // per BR-17 (e.g., a manufacturer with WELD #1 + WELD #2 has more than 7 stages,
      // but FINAL QC is always the last stage and signals that all work is done).
      const hasFinalQcComplete = trackers.some(
        t => t.stageName.toUpperCase() === FINAL_QC_STAGE_NAME && t.status === 'Complete'
      );
      if (!hasFinalQcComplete) allComplete = false;
      if (trackers.some(t => t.status === 'Complete')) anyStarted = true;
    }

    // A building shows "complete" once all its trusses' FINAL QC are done — the building's
    // production work is finished regardless of whether the order-level QC sign-off has been
    // recorded yet. The order-level sign-off is an administrative step, not per-building work.
    if (allComplete) statusMap.set(building.id, 'complete');
    else if (anyStarted) statusMap.set(building.id, 'in_progress');
    else statusMap.set(building.id, 'not_started');
  }

  return statusMap;
}

/** Exported for unit tests. */
export function resolveProductionStageStatus(
  tracker: TaskTrackerResponse | undefined,
  sequenceOrder: number,
  mergedStages: Array<{ tracker?: TaskTrackerResponse; order: number }>,
  productionReleased: boolean
): ProductionStageStatus {
  return resolveStageStatus(tracker, sequenceOrder, mergedStages, productionReleased);
}

export function transformBuildingListToBasicTraveler(
  buildings: BuildingWithoutTrusses[],
  order: Order,
  statusMap?: Map<number, BuildingCompletionStatus>
): TravelerBuilding[] {
  return buildings.map(b => ({
    id: b.id,
    name: b.name,
    description: b.additionalNotes ?? null,
    width: b.width ?? null,
    length: b.length ?? null,
    height: b.height ?? null,
    baySpacing: b.baySpacing,
    postSize: mapPostSize(b.postSize),
    geographicLocation: order.buildingState ?? null,
    liveLoad: order.liveLoad,
    deadLoad: order.deadLoad,
    windSpeed: order.windLoad,
    snowLoad: order.snowLoad,
    confirmedLoadRating: b.confirmedLoadRating ?? order.confirmedLoadRating ?? false,
    isPermanentDwelling: b.isPermanentDwelling,
    completionStatus: statusMap?.get(b.id) ?? 'not_started',
    gableTrusses: [],
    headerTrusses: [],
    leanToTrusses: [],
  }));
}
